import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Reveal, Starburst, Overline } from "../common";
import { useLead } from "../LeadDialog";
import { APARTMENT_SERVICES, IMAGES } from "../../data/site";

const Apartment = () => {
  const { openLead } = useLead();
  return (
    <section
      id="apartments"
      className="relative py-24 md:py-32 bg-white scroll-mt-20"
      data-testid="apartment-section"
    >
      <div className="max-w-[88rem] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Sticky text */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <Overline className="mb-5">Our Core Specialty</Overline>
              <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-jet-ink">
                Apartment Turnovers Done Right.
              </h2>
              <p className="mt-6 text-lg text-jet-ink/70 leading-relaxed max-w-md">
                Vacant units need to be ready quickly. We help apartment
                communities reduce downtime with dependable make-ready cleaning
                that keeps properties moving efficiently — units ready faster, a
                better resident experience.
              </p>

              <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
                {APARTMENT_SERVICES.map((s) => (
                  <li key={s} className="flex items-center gap-2.5 text-jet-ink/80 font-medium">
                    <span className="grid place-items-center h-5 w-5 rounded-full bg-jet-teal/15 text-jet-teal shrink-0">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                    {s}
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => openLead("quote")}
                className="mt-9 rounded-full bg-jet-teal hover:bg-jet-teal-deep text-white font-bold h-14 px-8 teal-glow group"
                data-testid="apartment-cta-button"
              >
                Request Apartment Pricing
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Button>
            </Reveal>
          </div>
        </div>

        {/* Scrolling images */}
        <div className="lg:col-span-7 space-y-6">
          {[
            { src: IMAGES.apartment1, label: "Move-Ready Units", stat: "Fast Turnaround" },
            { src: IMAGES.apartment2, label: "Leasing Offices & Clubhouses" },
            { src: IMAGES.apartment3, label: "Common Areas & Hallways" },
          ].map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className={`relative overflow-hidden rounded-3xl shadow-lg ${
                i === 0 ? "aspect-[16/11]" : "aspect-[16/9]"
              } ${i === 1 ? "lg:ml-16" : ""}`}
            >
              <img src={img.src} alt={img.label} loading="lazy" decoding="async" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-jet-ink/60 to-transparent" />
              <div className="absolute bottom-5 left-5 flex items-center gap-2">
                <Starburst className="h-4 w-4 text-jet-amber" />
                <span className="text-white font-bold text-sm md:text-base">{img.label}</span>
              </div>
              {img.stat && (
                <div className="absolute top-5 right-5 bg-white/90 backdrop-blur-md rounded-full px-4 py-2 text-xs font-bold text-jet-teal-deep uppercase tracking-wide">
                  {img.stat}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Apartment;
